import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
} from "@mui/material";
import { getQCReportById, getDefectReportSummary } from "../../api/qcReports";
import { useSnack } from "../../services/SnakbarProvider";

const QCReportDetail = ({ props }) => {
  const { id } = props || {};
  const { openSnack } = useSnack();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [defectSummary, setDefectSummary] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        const [reportResponse, summaryResponse] = await Promise.all([
          getQCReportById(id),
          getDefectReportSummary(id)
        ]);

        setReport(reportResponse.data);
        setDefectSummary(summaryResponse.data || []);
      } catch (error) {
        console.error("Error fetching report details:", error);
        openSnack("Error al cargar los detalles del reporte", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, openSnack]);

  const getHourlyCounts = (defect) => {
    return [
      defect.hour0, defect.hour1, defect.hour2, defect.hour3,
      defect.hour4, defect.hour5, defect.hour6, defect.hour7,
      defect.hour8, defect.hour9, defect.hour10, defect.hour11,
      defect.hour12, defect.hour13, defect.hour14, defect.hour15, defect.hour16
    ];
  };

  const getTotalCount = (defect) => {
    return getHourlyCounts(defect).reduce((sum, count) => sum + count, 0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (!report) {
    return (
      <Box p={2}>
        <Typography variant="h6" color="error">
          No se encontró el reporte
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Detalles del Reporte de Control de Calidad
      </Typography>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Información General
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  ID del Reporte
                </Typography>
                <Typography variant="body1">
                  {report.id}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Orden de Trabajo
                </Typography>
                <Typography variant="body1">
                  {report.workOrder}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Producto Terminado
                </Typography>
                <Typography variant="body1">
                  {report.finishedGoodName}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Empresa
                </Typography>
                <Typography variant="body1">
                  {report.companyName}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Fecha de Operación
                </Typography>
                <Typography variant="body1">
                  {new Date(report.operationDate).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Cantidad
                </Typography>
                <Typography variant="body1">
                  {report.quantity}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Inspector
                </Typography>
                <Typography variant="body1">
                  {report.inspectorName}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Turno
                </Typography>
                <Chip 
                  label={report.shift} 
                  color="primary" 
                  size="small"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Observations */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Observaciones
            </Typography>
            <Typography variant="body1">
              {report.observations || "Sin observaciones"}
            </Typography>
          </Paper>
        </Grid>

        {/* Defect Reports Summary */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Resumen de Defectos
            </Typography>
            {defectSummary.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Defecto</TableCell>
                      <TableCell align="center">Hora 1</TableCell>
                      <TableCell align="center">Hora 2</TableCell>
                      <TableCell align="center">Hora 3</TableCell>
                      <TableCell align="center">Hora 4</TableCell>
                      <TableCell align="center">Hora 5</TableCell>
                      <TableCell align="center">Hora 6</TableCell>
                      <TableCell align="center">Hora 7</TableCell>
                      <TableCell align="center">Hora 8</TableCell>
                      <TableCell align="center">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {defectSummary.map((defect, index) => {
                      const hourlyCounts = getHourlyCounts(defect);
                      const totalCount = getTotalCount(defect);
                      
                      return (
                        <TableRow key={index}>
                          <TableCell>{defect.defectName}</TableCell>
                          {hourlyCounts.slice(0, 8).map((count, hourIndex) => (
                            <TableCell key={hourIndex} align="center">
                              {count}
                            </TableCell>
                          ))}
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                            {totalCount}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body1" color="textSecondary">
                No hay reportes de defectos disponibles
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default QCReportDetail; 